import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Card, cardSurface } from '@/components/atoms/Card';
import ContentBlock from '@/components/molecules/ContentBlock';
import { DisclosurePanel } from '@/components/molecules/Disclosure';
import EmptyState from '@/components/molecules/EmptyState';
import { ToastInfo, ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { RequestError } from '@/domain/shared/errors';
import {
    useGetClientVideoStreamSettings,
    useUpdateBioscopeVideoStreamSettings,
    useUpdateLocationVideoStreamSettings,
    type VideoStreamSettingsValues,
} from '@/domain/videoStreamSettings';
import { areVideoStreamSettingsEqual } from '@/domain/videoStreamSettings/mappers';

import EditVideoStreamSettingsModal from './EditVideoStreamSettingsModal';
import VideoStreamSettingsTable from './VideoStreamSettingsTable';

import styles from './VideoStreamSettings.module.scss';

interface VideoStreamSettingsProps {
    clientId: number;
}

type EditTarget =
    | {
          mode: 'bioscope';
          title: string;
          bioscopeId: number;
          initial: VideoStreamSettingsValues;
      }
    | {
          mode: 'location';
          title: string;
          locationId: number;
          initial: VideoStreamSettingsValues;
          locationSettingsDiffer: boolean;
      };

function isForbidden(error: Error | RequestError | null): boolean {
    return RequestError.isRequestError(error) && error.status === 403;
}

export default function VideoStreamSettings(props: VideoStreamSettingsProps) {
    const { clientId } = props;
    const { t } = useTranslation();
    const infoSectionHeadingId = useId();
    const locationPanelIdBase = useId();
    const [expandedLocations, setExpandedLocations] = useState<Record<number, boolean>>({});
    const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

    const listQuery = useGetClientVideoStreamSettings(clientId);
    const updateBioscope = useUpdateBioscopeVideoStreamSettings();
    const updateLocation = useUpdateLocationVideoStreamSettings();

    const isSaving = updateBioscope.isLoading || updateLocation.isLoading;
    const groupedLocations = listQuery.data?.locations ?? [];
    const hasError = listQuery.isError && !isForbidden(listQuery.error);

    const locationInitialValues = useMemo(() => {
        return groupedLocations.reduce<Record<number, VideoStreamSettingsValues>>((acc, group) => {
            const first = group.bioscopes[0]?.settings;
            if (first) {
                acc[group.locationId] = first;
            }
            return acc;
        }, {});
    }, [groupedLocations]);

    if (isForbidden(listQuery.error)) {
        return null;
    }

    if (listQuery.isLoading) {
        return (
            <ContentBlock title={t('internal.videoStreamSettings.title')}>
                {t('internal.videoStreamSettings.loading')}
            </ContentBlock>
        );
    }

    if (hasError) {
        return (
            <ContentBlock title={t('internal.videoStreamSettings.title')}>
                <SomethingHappened
                    title={t('internal.videoStreamSettings.loadError')}
                    {...retryFailedQueries(listQuery)}
                />
            </ContentBlock>
        );
    }

    const onSave = async (values: VideoStreamSettingsValues) => {
        if (!editTarget) {
            return;
        }

        try {
            if (editTarget.mode === 'bioscope') {
                await updateBioscope.mutateAsync({
                    clientId,
                    bioscopeId: editTarget.bioscopeId,
                    requestBody: values,
                });
            } else {
                await updateLocation.mutateAsync({
                    clientId,
                    locationId: editTarget.locationId,
                    requestBody: values,
                });
            }

            ToastSuccess(
                t('internal.videoStreamSettings.saveSuccess'),
                'video-stream-settings-success'
            );
            setEditTarget(null);
        } catch {
            ToastInfo({
                content: t('internal.videoStreamSettings.saveError'),
                status: 'error',
                icon: 'noicon',
                options: { toastId: `video-stream-settings-error-${Date.now()}` },
            });
        }
    };

    const allLocationsExpanded =
        groupedLocations.length > 0 &&
        groupedLocations.every((g) => expandedLocations[g.locationId] === true);

    const toggleAllLocations = () => {
        setExpandedLocations(
            Object.fromEntries(groupedLocations.map((g) => [g.locationId, !allLocationsExpanded]))
        );
    };

    return (
        <ContentBlock title={t('internal.videoStreamSettings.title')}>
            <section
                {...cardSurface(
                    { variant: 'subtle', radius: 'sm', padding: 'none' },
                    styles.infoSection
                )}
                aria-labelledby={infoSectionHeadingId}>
                <h3 className={styles.infoTitle} id={infoSectionHeadingId}>
                    {t('internal.videoStreamSettings.info.title')}
                </h3>
                <ul className={styles.infoList}>
                    <li>
                        <span className={styles.infoTerm}>
                            {t('internal.videoStreamSettings.info.maxSessionTerm')}
                        </span>{' '}
                        {t('internal.videoStreamSettings.info.maxSession')}
                    </li>
                    <li>
                        <span className={styles.infoTerm}>
                            {t('internal.videoStreamSettings.info.dailyLimitTerm')}
                        </span>{' '}
                        {t('internal.videoStreamSettings.info.dailyLimit')}
                    </li>
                    <li>
                        <span className={styles.infoTerm}>
                            {t('internal.videoStreamSettings.info.usedTodayTerm')}
                        </span>{' '}
                        {t('internal.videoStreamSettings.info.usedToday')}
                    </li>
                    <li>
                        <span className={styles.infoTerm}>
                            {t('internal.videoStreamSettings.info.qualityTerm')}
                        </span>{' '}
                        {t('internal.videoStreamSettings.info.quality')}
                    </li>
                    <li>
                        <span className={styles.infoTerm}>
                            {t('internal.videoStreamSettings.info.noteTerm')}
                        </span>{' '}
                        {t('internal.videoStreamSettings.info.noteQuality')}
                        <br />
                        {t('internal.videoStreamSettings.info.noteMove')}
                    </li>
                </ul>
            </section>
            {groupedLocations.length === 0 ? (
                <EmptyState title={t('internal.videoStreamSettings.empty')} appearance="quiet" />
            ) : (
                <>
                    <div className={styles.globalLocationActions}>
                        <Button variant="secondary" onClick={toggleAllLocations}>
                            {allLocationsExpanded
                                ? t('internal.videoStreamSettings.collapseAll')
                                : t('internal.videoStreamSettings.expandAll')}
                        </Button>
                    </div>
                    {groupedLocations.map((group) => {
                        const isExpanded = expandedLocations[group.locationId] ?? false;
                        const initial = locationInitialValues[group.locationId];
                        const panelId = `${locationPanelIdBase}-${group.locationId}`;

                        return (
                            <Card
                                key={group.locationId}
                                radius="sm"
                                padding="none"
                                className={styles.locationSection}>
                                <div className={styles.locationHeader}>
                                    <div>
                                        <h3 className={styles.locationTitle}>
                                            {group.locationName}
                                        </h3>
                                        <span className={styles.locationMeta}>
                                            {t('internal.videoStreamSettings.timezone', {
                                                timezone: group.timezone,
                                            })}
                                        </span>
                                    </div>
                                    <div className={styles.locationActions}>
                                        <Button
                                            variant="secondary"
                                            aria-expanded={isExpanded}
                                            aria-controls={panelId}
                                            onClick={() =>
                                                setExpandedLocations((previous) => ({
                                                    ...previous,
                                                    [group.locationId]: !isExpanded,
                                                }))
                                            }>
                                            {isExpanded
                                                ? t('internal.videoStreamSettings.collapse')
                                                : t('internal.client.expand')}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            disabled={!initial}
                                            onClick={() => {
                                                if (!initial) return;
                                                const locationSettingsDiffer =
                                                    group.bioscopes.length > 1 &&
                                                    group.bioscopes.some(
                                                        (b) =>
                                                            !areVideoStreamSettingsEqual(
                                                                b.settings,
                                                                initial
                                                            )
                                                    );
                                                setEditTarget({
                                                    mode: 'location',
                                                    locationId: group.locationId,
                                                    title: t(
                                                        'internal.videoStreamSettings.editLocationTitle'
                                                    ),
                                                    initial,
                                                    locationSettingsDiffer,
                                                });
                                            }}>
                                            {t('internal.videoStreamSettings.editAll')}
                                        </Button>
                                    </div>
                                </div>
                                <DisclosurePanel id={panelId} isOpen={isExpanded}>
                                    {isExpanded && (
                                        <VideoStreamSettingsTable
                                            bioscopes={group.bioscopes}
                                            timezone={group.timezone}
                                            onEdit={(row) =>
                                                setEditTarget({
                                                    mode: 'bioscope',
                                                    bioscopeId: row.bioscope.id,
                                                    title: t(
                                                        'internal.videoStreamSettings.editBioscopeTitle'
                                                    ),
                                                    initial: row.settings,
                                                })
                                            }
                                        />
                                    )}
                                </DisclosurePanel>
                            </Card>
                        );
                    })}
                </>
            )}
            {editTarget && (
                <EditVideoStreamSettingsModal
                    isOpen={true}
                    title={editTarget.title}
                    initialValues={editTarget.initial}
                    isSaving={isSaving}
                    onClose={() => setEditTarget(null)}
                    onSubmit={onSave}
                    locationSettingsDiffer={
                        editTarget.mode === 'location' ? editTarget.locationSettingsDiffer : false
                    }
                />
            )}
        </ContentBlock>
    );
}
